import { enqueueSnackbar } from 'notistack'
import { axiosInstance } from './axiosInstance'
import { AxiosResponse } from 'axios'
import {
    GetUserOrganizations,
    GetUserOrganizationsPayload,
    GetUserPermissions,
    GetUserTransferOrganizationsPayload,
} from './apiInterfaces'
import { USER_SESSION_AUDIT } from '../constants/constants'
import { NewAPITokenData } from '../components/Layout/Navbar/APIToken'
import i18n from '../i18n'
import { UserData } from '../redux/slices/userSlice'

//Reuse below utitlity functions and utility functions in dtServices
// Utility functions

const handleResponse = <T>(
    response: AxiosResponse<T>,
    successMessage?: string,
    useApiMessage?: boolean,
): T => {
    if (response.status >= 200 && response.status < 300) {
        const apiMessage = (response.data as { message?: string } | undefined)
            ?.message
        const message =
            useApiMessage && apiMessage ? i18n.t(apiMessage) : successMessage
        if (response && message) {
            enqueueSnackbar(message, { variant: 'success' })
        }
        return response.data
    } else {
        throw new Error('Error')
    }
}

const handleError = (
    err: any,
    errorMessage?: string,
    useApiMessage?: boolean,
) => {
    const apiMessage = err.response?.data?.message
    const translatedApiMessage = apiMessage ? i18n.t(apiMessage) : undefined
    const message = useApiMessage
        ? translatedApiMessage || errorMessage || 'An error occurred'
        : errorMessage || apiMessage || 'An error occurred'
    enqueueSnackbar(message, { variant: 'error' })
    // throw new Error(message)
}

export const makeRequest = async <T>(
    method: 'get' | 'post' | 'put' | 'delete',
    url: string,
    data?: unknown,
    params?: unknown,
    successMessage?: string,
    errorMessage?: string,
    useApiMessage?: boolean,
): Promise<T> => {
    try {
        const response: AxiosResponse<T> = await axiosInstance({
            method,
            url,
            data,
            params,
        })
        return handleResponse(response, successMessage, useApiMessage)
    } catch (err) {
        handleError(err, errorMessage, useApiMessage)
        return undefined as unknown as T
    }
}

export const fetchUsers = async (params: unknown): Promise<any> => {
    const response: any = await makeRequest('get', `/users`, null, params)
    return response
}

export const updateUser = async (
    payload: unknown,
    orgId: string,
): Promise<void> => {
    await makeRequest<void>(
        'put',
        `/users`,
        payload,
        {
            orgId,
        },
        i18n.t('userUpdateSuccess'),
        i18n.t('userUpdateError'),
    )
}

export const resendUserInvitation = async (
    payload: { id: number; roleAuth0Id: string },
    orgId: string,
): Promise<void> => {
    await makeRequest<void>(
        'put',
        `/users`,
        {
            ...payload,
            resendInvitation: true,
        },
        {
            orgId,
        },
        i18n.t('invitationResentSuccess'),
        i18n.t('invitationResentError'),
    )
}

export const changeOwnPassword = async (): Promise<void> => {
    await makeRequest<void>(
        'post',
        `/users/change-password`,
        '',
        null,
        i18n.t('passwordResetSuccess'),
        i18n.t('passwordResetError'),
    )
}

export const resetUserPassword = async (
    payload: { email: string; userId: number },
    orgId: string,
): Promise<void> => {
    await makeRequest<void>(
        'post',
        `/users/change-password/admin`,
        payload,
        {
            orgId,
        },
        i18n.t('passwordResetSuccess'),
        i18n.t('passwordResetError'),
    )
}

export const deleteUser = async (
    members: unknown,
    orgId: string,
): Promise<void> => {
    await makeRequest<any>(
        'delete',
        `/users`,
        { members },
        {
            orgId,
        },
        i18n.t('userDeleteSuccess'),
        i18n.t('userDeleteError'),
    )
}

export const inviteUser = async (
    data: unknown,
    orgId: string,
): Promise<{ message?: string } | void> => {
    return await makeRequest<{ message?: string }>(
        'post',
        `/users/inviteUser`,
        data,
        {
            orgId,
        },
        i18n.t('invitationSentSuccess'),
        i18n.t('invitationSentError'),
        true,
    )
}

export const fetchUserOrganizations = async ({
    pageNumber = 1,
    size = 10,
    currentOrgId = '',
    searchText = '',
    includeVirtualOrgs = false,
    onlyEnterprises = false,
}: GetUserOrganizationsPayload): Promise<GetUserOrganizations> => {
    const response = await makeRequest<GetUserOrganizations>(
        'get',
        `/users/organizations`,
        null,
        {
            pageNumber,
            size,
            currentOrgId,
            includeVirtualOrgs,
            searchText,
            onlyEnterprises,
        },
    )
    return response
}

export const fetchUserTransferOrganizations = async ({
    templateId,
    pageNumber = 1,
    size = 10,
    currentOrgId = '',
    searchText = '',
    includeVirtualOrgs = false,
}: GetUserTransferOrganizationsPayload): Promise<GetUserOrganizations> => {
    const response = await makeRequest<GetUserOrganizations>(
        'get',
        `/organization/transfer-organizations`,
        null,
        {
            entityTemplateId: templateId,
            pageNumber,
            size,
            currentOrgId,
            includeVirtualOrgs,
            searchText,
        },
    )
    return response
}

export const fetchUserOrgPermissions = async (
    orgId: string,
): Promise<GetUserPermissions> => {
    const response = await makeRequest<GetUserPermissions>(
        'get',
        `/users/permissions`,
        null,
        {
            orgId,
        },
    )
    return response
}

export const fetchSuperSetGuestToken = async (
    dashboardId: string,
    orgId: string,
): Promise<string> => {
    const response = await makeRequest<{ token: string }>(
        'get',
        `/users/custom/token`,
        null,
        {
            id: dashboardId,
            orgId,
        },
    )

    return response?.token
}

export const getUserDataByEmail = async (
    orgId: string,
    email: string,
    language?: string,
): Promise<UserData> => {
    const response = await makeRequest<UserData>(
        'get',
        `/users/userByEmail`,
        null,
        {
            orgId,
            email,
            language, // add language to params
        },
    )
    return response
}

export const updateUserUnits = async (
    payload: unknown,
    userId: string,
): Promise<void> => {
    await makeRequest<void>(
        'put',
        `/users/updateUnits?userId=${userId}`,
        payload,
        null,
        i18n.t('userUpdateSuccess'),
        i18n.t('userUpdateError'),
    )
}

export const updateUserSessionsAudit = async (
    action: USER_SESSION_AUDIT,
): Promise<void> => {
    await makeRequest<void>('put', `/users/audit/sessions`, null, { action })
}

export const getRequestAPIAccess = async (
    orgId: string,
    userId?: number,
): Promise<NewAPITokenData> => {
    const url = `/users/userApiToken`
    const response = await makeRequest<NewAPITokenData>('get', url, null, {
        orgId,
        userId,
    })
    return response
}
export const generateRequestAPIAccess = async (
    orgId: string,
    userId?: number,
): Promise<NewAPITokenData> => {
    const url = `/users/userApiToken`
    const response = await makeRequest<NewAPITokenData>('post', url, null, {
        orgId,
        userId,
    })
    return response
}
export const deleteRequestAPIAccess = async (
    orgId: string,
    userId?: number,
): Promise<NewAPITokenData> => {
    const url = `/users/userApiToken`
    const response = await makeRequest<NewAPITokenData>('delete', url, null, {
        orgId,
        userId,
    })
    return response
}
